import { Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { PageHeaderStatic, WidgetIcon } from '@procureai/ui-mui';

import { NegotiationDomainType } from '~/common/types';
import { DataGridContextProvider } from '~/components/DataGridCustom';
import { DataGridQueryBuilder, useDataGridQueryBuilderQuery } from '~/components/DataGridQueryBuilder';
import { DataGridWidget } from '~/components/DataGridWidget';
import { DomainIcon } from '~/components/DomainIcon/DomainIcon';
import { Filters, FilterSaveStatus, useFilters } from '~/components/Filters';
import { NegotiationPageTitle } from '~/components/NegotiationPageTitle';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { useProcessStepsAutoRefresh } from '~/hooks/useProcessStepsAutoRefresh/useProcessStepsAutoRefresh';
import { ProcessStepsAutoRefreshVariant } from '~/hooks/useProcessStepsAutoRefresh/useProcessStepsAutoRefresh.util';
import { RouteId } from '~/routes';

import { useDemandDataGridConfig } from './NegotiationDemandList.dataGrid';
import { useDemandDataGrid } from './NegotiationDemandList.hooks';
import { NEGOTIATION_DEMAND_LIST_QUERY } from './NegotiationDemandList.query.gql';
import { useNegotiationDemandDataGridQueryBuilderConfig } from './NegotiationDemandList.queryBuilder';

const domain = NegotiationDomainType.DEMAND;

export const NegotiationDemandList = () => {
  const apiRef = useGridApiRef();
  const { queryId, query, onQueryChange, onQueryIdChange, rulesTotal, loading } = useDataGridQueryBuilderQuery({
    domain,
  });
  const { onFilterSave, saveStatus, selected, ...filtersProps } = useFilters({ domain, queryId });

  const { contextValue } = useDemandDataGrid({ apiRef });

  useProcessStepsAutoRefresh({
    variant: ProcessStepsAutoRefreshVariant.DEMAND_LIST,
  });

  return (
    <PageLayout
      fullHeight
      renderHeader={
        <>
          <PageHeaderStatic
            title={<NegotiationPageTitle domain={domain} />}
            maxWidth={false}
            icon={<WidgetIcon icon={<DomainIcon domain={domain} />} />}
            right={
              <Stack
                spacing={3}
                direction="row"
                sx={{
                  overflow: 'hidden',
                  alignItems: 'center',
                }}
              >
                <Filters
                  {...filtersProps}
                  selected={selected}
                  onFilterSelect={(filter) => {
                    onQueryIdChange(filter.queryId);
                  }}
                />
              </Stack>
            }
          />
          <DataGridQueryBuilder
            query={query}
            clearDisabled={rulesTotal === 0}
            saveDisabled={saveStatus === FilterSaveStatus.DISABLED}
            onQueryChange={onQueryChange}
            onSave={onFilterSave}
            selectedFilterName={selected?.name}
            useConfig={useNegotiationDemandDataGridQueryBuilderConfig}
          />
        </>
      }
    >
      <DataGridContextProvider value={contextValue}>
        <DataGridWidget
          apiRef={apiRef}
          domain={domain}
          loading={loading}
          filter={query}
          filterCount={rulesTotal}
          useConfig={useDemandDataGridConfig}
          initialNavigateDetail={({ row }) => ({
            to: RouteId.demandDetail(row.id),
            trackingOptions: { domain, id: row.id },
          })}
          loadingProps={{
            query: NEGOTIATION_DEMAND_LIST_QUERY,
            getNodes: (data) => data.tesseract.demands.nodes,
            getTotalCount: (data) => data.tesseract.demands.totalCount || 0,
          }}
        />
      </DataGridContextProvider>
    </PageLayout>
  );
};
